import { parseSearchParam } from '@lib/utils/url';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

interface UseConsumeCodeOptions {
  /** Store method that exchanges the URL `code` for a session (sets userStore.data). */
  consume: (code: string) => Promise<unknown>;
  /** Where to go when the URL has no `code` at all. */
  missingCodeRedirect: string;
  /** Default landing route on success. Skipped when a `redirect` param is present. */
  successRedirect: string;
  /** Map a thrown error to a user-facing message. Omit for a generic fallback. */
  errorToMessage?: (error: unknown) => string;
}

interface UseConsumeCodeResult {
  error: string | null;
}

/**
 * Mount-time "consume a code from the URL" lifecycle shared by the magic-link
 * and confirm-email landing pages: reads `?code=`, guards against a double
 * consume, calls the store method, and surfaces an error message on failure.
 *
 * On success it navigates to the internal `successRedirect`. Any pending
 * external redirect (`?redirect=`, e.g. chat) is NOT performed here — it's kept
 * in the durable stash (see userStore) and performed by the WithUser post-auth
 * flow AFTER it gates on verification. So this page just lands the user
 * internally and lets WithUser orchestrate where they actually go.
 */
export function useConsumeCode({
  consume,
  missingCodeRedirect,
  successRedirect,
  errorToMessage,
}: UseConsumeCodeOptions): UseConsumeCodeResult {
  const { search } = useLocation();
  const params = parseSearchParam(search);
  const code = params.get('code');
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;

    if (!code) {
      navigate(missingCodeRedirect);
      return;
    }

    consume(code)
      .then(() => {
        navigate(successRedirect);
      })
      .catch((e) => {
        setError(errorToMessage?.(e) ?? 'Something went wrong. Please try again.');
      });
  }, [code, navigate, consume, missingCodeRedirect, successRedirect, errorToMessage]);

  return { error };
}
